// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 41-groups-management.md
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Lock, Shield, UserRound } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { MonogramTile } from '@/components/greenlight/MonogramTile';
import { MonoValue } from '@/components/greenlight/MonoValue';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useAuth, useCurrentUser, startImpersonation } from '@/lib/auth';
import { initials } from '@/lib/hueFor';

import { UserGroupsPanel } from './UserGroupsPanel';
import { ROLE_RANK, type OrgRole, type RoleSource, type UserListItem } from '@greenlight/shared';

export interface UserInspectorBodyProps {
  user: UserListItem;
  /** Called after a successful mutation so the parent can update its selectedUser snapshot. */
  onUserUpdated?: (updated: UserListItem) => void;
  /** Override the org's role-management mode; otherwise read from settings. */
  roleSource?: RoleSource;
  /** When true, settings are still loading; the role row shows a skeleton. */
  roleLoading?: boolean;
}

/** Mono uppercase eyebrow section header with an optional right slot. */
function SectionHeader({
  children,
  right,
}: {
  children: React.ReactNode;
  right?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between mb-2">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.07em] text-muted-foreground font-medium">
        {children}
      </span>
      {right != null && <span>{right}</span>}
    </div>
  );
}

/** A single key/value row inside the identity dl. */
function IdentityRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <dt className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-muted-foreground shrink-0">
        {label}
      </dt>
      <dd className="text-[12.5px] text-foreground text-right min-w-0">{children}</dd>
    </div>
  );
}

const ROLES = [
  { value: 'org_admin' as const, label: 'Org admin' },
  { value: 'admin' as const, label: 'Admin' },
  { value: 'user' as const, label: 'User' },
];

/** Human message for a failed role change, mapping the ceiling/guard codes. */
function roleChangeErrorMessage(body: unknown): string {
  const code =
    body !== null && typeof body === 'object' && 'code' in body
      ? (body as { code: unknown }).code
      : undefined;
  if (code === 'users.role_above_caller') return 'You can only assign roles at or below your own.';
  if (code === 'users.last_org_admin')
    return "You can't remove the last org admin. Assign another first.";
  return "Couldn't update the role.";
}

/**
 * Inspector body for a single user. Covers Identity (with inline role edit for
 * org_admin), Groups, Apps owned, and App Access sections. App Access lists the
 * restricted apps this person is named on; the per-user role remains manually
 * assignable until the org configures group→role mapping.
 */
export function UserInspectorBody({
  user,
  onUserUpdated,
  roleSource: roleSourceProp,
  roleLoading: roleLoadingProp,
}: UserInspectorBodyProps): React.JSX.Element {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const hasGovernanceAppAccess = user.role === 'org_admin' || user.role === 'admin';
  const isDeactivated = !user.is_active;

  // Self-fetch settings to determine the org's role-management mode when the
  // caller doesn't supply it (the cache is shared, so no extra request).
  const { data: settingsData, isLoading: settingsLoading } = api.settings.get.useQuery(
    ['settings-users'],
    undefined,
    { queryKey: ['settings'], enabled: roleSourceProp === undefined },
  );
  const selfSettings = settingsData?.status === 200 ? settingsData.body : undefined;
  const roleSource: RoleSource | undefined =
    roleSourceProp !== undefined ? roleSourceProp : selfSettings?.role_source;
  const roleLoading = roleLoadingProp !== undefined ? roleLoadingProp : settingsLoading;

  // The assignment ceiling (docs/10 §Greenlight-managed roles): a caller may set
  // or modify only roles at or below their own. `org_admin` > `admin` > `user`.
  const callerRole = currentUser?.role as OrgRole | undefined;
  const callerRank = callerRole ? ROLE_RANK[callerRole] : 0;
  const targetAboveCaller = ROLE_RANK[user.role] > callerRank;
  const roleOptions = ROLES.filter((r) => ROLE_RANK[r.value] <= callerRank);

  const isGreenlight = roleSource === 'greenlight';
  const isIdp = roleSource === 'idp';
  // Who may edit: Greenlight-managed lets org_admin and admin assign within the
  // ceiling; IdP-managed keeps manual override to org_admin (and surfaces it as
  // IdP-managed). The server re-checks regardless.
  const canManage = isGreenlight
    ? callerRole === 'org_admin' || callerRole === 'admin'
    : callerRole === 'org_admin';
  // Under Greenlight management, the picker is disabled for a user whose current
  // role outranks the caller (the server returns users.role_above_caller).
  const pickerDisabled = targetAboveCaller;

  // IdP-managed: roles render as IdP-managed; a manual org_admin edit lasts only
  // until the user's next login. We surface this as a read-only label.
  const roleLockedByIdp = !roleLoading && isIdp;

  // Mutation state
  const updateMutation = api.users.update.useMutation();
  const [roleChanging, setRoleChanging] = React.useState(false);
  const [deactivateOpen, setDeactivateOpen] = React.useState(false);
  const [activating, setActivating] = React.useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [impersonateOpen, setImpersonateOpen] = React.useState(false);
  const [impersonating, setImpersonating] = React.useState(false);
  const canImpersonate =
    callerRole === 'org_admin' &&
    user.is_active &&
    (user.role === 'admin' || user.role === 'user') &&
    user.id !== currentUser?.id;
  const targetLabel = user.display_name ?? user.email;

  const handleStartImpersonation = async (): Promise<void> => {
    if (impersonating) return;
    setImpersonating(true);
    try {
      const res = await startImpersonation(user.id);
      if (!res.ok) {
        toast.error("Couldn't start impersonation.");
        setImpersonating(false);
        return;
      }
      queryClient.clear();
      await refresh();
      navigate('/apps');
    } catch {
      toast.error("Couldn't start impersonation.");
      setImpersonating(false);
    }
  };

  const handleRoleChange = async (newRole: string): Promise<void> => {
    if (newRole === user.role || roleChanging) return;
    setRoleChanging(true);
    try {
      const res = await updateMutation.mutateAsync({
        params: { id: user.id },
        body: { role: newRole as UserListItem['role'] },
      });
      if (res.status === 200) {
        const updated = { ...user, role: newRole as UserListItem['role'] };
        toast.success(
          `Role changed to ${ROLES.find((r) => r.value === newRole)?.label ?? newRole}.`,
        );
        void queryClient.invalidateQueries({ queryKey: ['users'] });
        onUserUpdated?.(updated);
      } else {
        toast.error(roleChangeErrorMessage(res.body));
      }
    } catch (err) {
      toast.error(roleChangeErrorMessage((err as { body?: unknown } | null)?.body));
    } finally {
      setRoleChanging(false);
    }
  };

  const handleToggleActive = async (activate: boolean): Promise<void> => {
    setActivating(true);
    try {
      const res = await updateMutation.mutateAsync({
        params: { id: user.id },
        body: { isActive: activate },
      });
      if (res.status === 200) {
        const updated = { ...user, is_active: activate };
        toast.success(
          activate
            ? `${user.display_name ?? user.email} activated.`
            : `${user.display_name ?? user.email} deactivated.`,
        );
        void queryClient.invalidateQueries({ queryKey: ['users'] });
        setDeactivateOpen(false);
        onUserUpdated?.(updated);
      } else {
        toast.error(activate ? "Couldn't activate the user." : "Couldn't deactivate the user.");
      }
    } catch {
      toast.error(activate ? "Couldn't activate the user." : "Couldn't deactivate the user.");
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* IDENTITY */}
      <section>
        <SectionHeader
          right={
            isIdp ? (
              <span className="inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground">
                <Lock className="h-3.5 w-3.5" aria-hidden />
                IdP-managed
              </span>
            ) : isGreenlight ? (
              <span className="inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground">
                Greenlight-managed
              </span>
            ) : null
          }
        >
          Identity
        </SectionHeader>
        <div className="rounded-gl border border-border bg-muted/40 overflow-hidden">
          <dl className="divide-y divide-border text-[12.5px] px-4">
            <IdentityRow label="Email">{user.email}</IdentityRow>
            <IdentityRow label="Role">
              {roleLoading ? (
                <div className="h-6 w-[100px] rounded bg-muted motion-safe:animate-pulse" />
              ) : roleLockedByIdp ? (
                <div className="text-right">
                  <span className="text-muted-foreground">
                    {ROLES.find((r) => r.value === user.role)?.label ?? user.role}
                  </span>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Role is assigned by your IdP group mapping.
                    <br />
                    Manual edits last only until the user&apos;s next login.
                  </p>
                </div>
              ) : isGreenlight && canManage ? (
                <div className="text-right">
                  <Select
                    value={user.role}
                    onValueChange={handleRoleChange}
                    disabled={roleChanging || pickerDisabled}
                  >
                    <SelectTrigger className="h-8 w-[120px] text-[12.5px]" aria-label="Change role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {pickerDisabled && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      This user&apos;s role is above yours — ask an org admin to change it.
                    </p>
                  )}
                </div>
              ) : (
                <span className="text-muted-foreground">
                  {ROLES.find((r) => r.value === user.role)?.label ?? user.role}
                </span>
              )}
            </IdentityRow>
            <IdentityRow label="IdP subject">
              {user.idp_subject != null ? (
                <MonoValue copyable>{user.idp_subject}</MonoValue>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </IdentityRow>
          </dl>
        </div>

        {/* Deactivate / Activate — managers, within the ceiling (the server
            re-checks; the control hides for a target above the caller's role). */}
        {canManage && !targetAboveCaller && (
          <div className="mt-3">
            {isDeactivated ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleToggleActive(true)}
                disabled={activating}
                className="h-11 md:h-9 w-full"
              >
                {activating ? 'Activating…' : 'Reactivate user'}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeactivateOpen(true)}
                className="h-11 md:h-9 w-full text-destructive border-destructive/30 hover:bg-destructive/10"
              >
                Deactivate user…
              </Button>
            )}
          </div>
        )}

        {/* Log in as (impersonation) — org_admin only, eligible targets only. */}
        {canImpersonate && (
          <div className="mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setImpersonateOpen(true)}
              className="h-11 md:h-9 w-full gap-1.5"
              data-testid="log-in-as"
            >
              <UserRound className="size-4" aria-hidden />
              Log in as {targetLabel}
            </Button>
          </div>
        )}
      </section>

      {/* Log in as confirm dialog */}
      <Dialog open={impersonateOpen} onOpenChange={setImpersonateOpen}>
        <DialogContent className="rounded-[18px] ad-shadow">
          <DialogHeader>
            <DialogTitle>Log in as {targetLabel}?</DialogTitle>
            <DialogDescription>
              You&apos;ll see the dashboard exactly as {targetLabel} ({user.role}) does, under their
              RBAC. Every action you take is{' '}
              <span className="text-foreground">audited under your own identity</span>. A banner
              stays on screen until you stop.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setImpersonateOpen(false)}
              disabled={impersonating}
            >
              Cancel
            </Button>
            <Button onClick={() => void handleStartImpersonation()} disabled={impersonating}>
              {impersonating ? 'Starting…' : `Log in as ${targetLabel}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate confirm dialog */}
      <Dialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
        <DialogContent className="rounded-[18px] ad-shadow">
          <DialogHeader>
            <DialogTitle>Deactivate {user.display_name ?? user.email}?</DialogTitle>
            <DialogDescription>
              This ends every active agent session for this user and revokes their app access. They
              will not be able to sign in until reactivated. This is audited.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeactivateOpen(false)} disabled={activating}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleToggleActive(false)}
              disabled={activating}
            >
              {activating ? 'Deactivating…' : `Deactivate ${user.display_name ?? user.email}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GROUPS */}
      <section>
        <SectionHeader>Groups</SectionHeader>
        <UserGroupsPanel
          user={user}
          canManage={currentUser?.role === 'org_admin'}
          onUserUpdated={onUserUpdated}
        />
      </section>

      {/* APPS OWNED — ownership (apps.owner_id / app_co_owners) is a stronger,
          Greenlight-native relationship than reach-via-group (App Access below), so
          it renders in its own section and is not conflated with access. Ownership
          persists regardless of active status. Each row links to the app detail. */}
      <section>
        <SectionHeader>Apps owned</SectionHeader>
        {user.owned_apps == null ? (
          <p className="text-[12.5px] text-muted-foreground">
            Ownership information isn&apos;t available.
          </p>
        ) : user.owned_apps.length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground">
            Doesn&apos;t own or co-own any apps.
          </p>
        ) : (
          <div className="rounded-gl border border-border bg-card divide-y divide-border overflow-hidden">
            {user.owned_apps.map((app) => (
              <Link
                key={app.app_id}
                to={`/apps/${app.app_id}`}
                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
              >
                <MonogramTile
                  seed={app.app_slug}
                  label={initials(app.app_name)}
                  size={32}
                  shape="rounded"
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-foreground truncate">{app.app_name}</p>
                  <p className="mt-0.5 font-mono text-[10.5px] text-muted-foreground truncate">
                    {app.app_slug}
                  </p>
                </div>
                {app.is_co_owner && (
                  <span className="shrink-0 rounded bg-muted px-1.5 py-px font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                    {app.via_group ? `Editor via ${app.via_group.name}` : 'Editor'}
                  </span>
                )}
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* APP ACCESS */}
      <section>
        <SectionHeader>App Access</SectionHeader>
        {/* Deactivated wins over role: a deactivated account has no access even if it
            still carries admin / org_admin — matches AppAccessCluster in the list row. */}
        {isDeactivated ? (
          <div className="rounded-gl border border-border bg-muted/30 p-3 text-[12.5px] leading-relaxed text-foreground">
            Account is deactivated in your IdP. All app access is revoked.
          </div>
        ) : hasGovernanceAppAccess ? (
          <div className="rounded-gl border border-primary/30 bg-primary/[0.06] flex items-start gap-2.5 p-3 text-[12.5px] leading-relaxed text-foreground">
            <Shield className="h-4 w-4 shrink-0 mt-0.5 text-primary" aria-hidden />
            <p>
              {user.role === 'org_admin' ? 'Org admin' : 'Admin'} — governance access to all apps.
            </p>
          </div>
        ) : user.accessible_apps && user.accessible_apps.length > 0 ? (
          <div className="rounded-gl border border-border bg-card divide-y divide-border overflow-hidden">
            {user.accessible_apps.map((app) => (
              <div key={app.app_id} className="flex items-center gap-3 px-4 py-3">
                <MonogramTile
                  seed={app.app_slug}
                  label={initials(app.app_name)}
                  size={32}
                  shape="rounded"
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-foreground truncate">{app.app_name}</p>
                  <p className="font-mono text-[10.5px] text-muted-foreground">{app.app_slug}</p>
                </div>
              </div>
            ))}
          </div>
        ) : user.accessible_apps != null ? (
          <p className="text-[12.5px] text-muted-foreground">No app access assigned.</p>
        ) : (
          <p className="text-[12.5px] text-muted-foreground">
            App access information isn&apos;t available.
          </p>
        )}
      </section>

      {/* Footer note — always shown */}
      <p className="text-[11.5px] text-muted-foreground leading-relaxed mt-2.5">
        Read-only here. Apps open to the whole org aren&apos;t listed — everyone can reach those. To
        change who can open an app, its owner or an admin names people on that app&apos;s{' '}
        <span className="text-foreground">Access</span> area under Apps.
      </p>
    </div>
  );
}
